/**
 * Extracts date of birth (YYYY-MM-DD) from a South African-style ID number (YYMMDD#######) 
 * or passport number (YYMMDD#######).
 * Returns null for passport numbers that don't follow the date pattern.
 * 
 * @param {string} idNumber - The ID or passport number to extract DOB from
 * @returns {string|null} Date of birth in YYYY-MM-DD format, null for non-date passport numbers, or empty string if invalid
 */
export function extractDOBFromId(idNumber) {
  if (!idNumber || typeof idNumber !== 'string') {
    return "";
  }
  
  // Handle both ID numbers and passport numbers
  // ID numbers are typically 13 digits, passport numbers can vary but often follow similar pattern
  // For now, we'll focus on the common YYMMDD pattern at the beginning
  
  // Check if it starts with a date pattern (YYMMDD)
  const datePattern = /^(\d{2})(\d{2})(\d{2})/;
  const match = idNumber.match(datePattern);
  
  if (!match) {
    // If no date pattern found, check if it's a valid passport number
    const cleanInput = idNumber.replace(/[^a-zA-Z0-9]/g, '');
    if (cleanInput.length >= 5 && cleanInput.length <= 20) {
      // Valid passport number but no date pattern - return null
      return null;
    }
    // Invalid format - return empty string
    return "";
  }
  
  const [, yy, mm, dd] = match;
  
  // Validate month and day
  const month = parseInt(mm, 10);
  const day = parseInt(dd, 10);
  
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return "";
  }
  
  const now = new Date();
  const currentYear = now.getFullYear() % 100;
  
  // Determine century based on current year
  // If YY is greater than current year, assume 1900s, otherwise 2000s
  // This is a reasonable assumption for most cases
  const century = parseInt(yy, 10) > currentYear ? 1900 : 2000;
  const fullYear = century + parseInt(yy, 10);
  
  // Validate the complete date
  const date = new Date(`${fullYear}-${mm}-${dd}`);
  if (
    date.getFullYear() !== fullYear ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day ||
    date > now // Date of birth cannot be in the future
  ) {
    return "";
  }
  
  return date.toISOString().slice(0, 10);
}

/**
 * Determines if the input is likely a valid ID number or passport number
 * 
 * @param {string} input - The input to validate
 * @returns {boolean} True if the input appears valid
 */
export function isValidIdOrPassport(input) {
  if (!input || typeof input !== 'string') {
    return false;
  }
  
  // Remove any non-alphanumeric characters
  const cleanInput = input.replace(/[^a-zA-Z0-9]/g, '');
  
  // Check if it starts with a date pattern (ID number format)
  const datePattern = /^(\d{2})(\d{2})(\d{2})/;
  const match = cleanInput.match(datePattern);
  
  if (match) {
    // Valid ID number format - validate month and day
    const [, , mm, dd] = match;
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    
    return month >= 1 && month <= 12 && day >= 1 && day <= 31;
  }
  
  // If not ID number format, accept as passport number if it has reasonable length
  // Passport numbers can vary in format, so we accept any alphanumeric string
  return cleanInput.length >= 5 && cleanInput.length <= 20;
} 